import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api, getUser, setToken, saveUser, logout } from "../api";
import { ImageCropModal } from "../components/ImageCropModal";
import { CameraCapture } from "../components/CameraCapture";
import { ORG_TYPE_CODES } from "../lib/orgTypes";
import { toast } from "sonner";

export function PhotoSection() {
  const { t } = useTranslation();
  const [me, setMe] = useState(null);
  const [crop, setCrop] = useState(null); // { kind, blob?, source? }
  const [camera, setCamera] = useState(false);
  const load = () => api.get("/auth/me").then(({data}) => {
    setMe(data);
    saveUser({ ...getUser(), avatar_url: data.avatar_url, cover_url: data.cover_url, profile_complete: data.profile_complete });
    window.dispatchEvent(new Event("user-updated"));
  });
  useEffect(() => { load(); }, []);
  const upload = (kind, source) => async (blob) => {
    const fd = new FormData();
    fd.append("file", new File([blob], `${kind}.jpg`, { type: "image/jpeg" }));
    if (source) fd.append("source", source);
    await api.post(`/me/${kind}`, fd);
    toast.success(t("photo.upload_ok"));
    await load();
  };
  if (!me) return null;
  const canCover = me.role === "organization";
  const canCamera = me.role === "user" || me.role === "professional";  // strutture: solo dispositivo
  const incomplete = me.profile_complete === false;
  return (<div className={`yp-card p-6 space-y-4 mb-4 ${incomplete ? "border-2 border-blue-400" : ""}`} data-testid="photo-section">
    {incomplete && (
      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800" data-testid="profile-completion-banner">
        <div className="font-semibold mb-1">{t("photo.complete_title")}</div>
        <div>{!me.avatar_url ? t("photo.complete_avatar") : (canCover ? t("photo.complete_cover") : t("photo.complete_avatar"))}</div>
        <div className="text-xs mt-1 text-blue-600">{t("photo.complete_limited")}</div>
      </div>)}
    <h3 className="font-semibold">{t("photo.add")}</h3>
    <p className="text-xs text-slate-500">{t("photo.privacy_notice")}</p>
    <div className="flex items-center gap-4 flex-wrap">
      {me.avatar_url
        ? <img src={me.avatar_url} alt="" className="w-16 h-16 rounded-full object-cover" data-testid="settings-avatar" />
        : <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center text-xl font-bold text-slate-500" data-testid="settings-avatar-placeholder">{(me.nickname || me.email || "?")[0].toUpperCase()}</div>}
      <div className="flex gap-2 flex-wrap">
        <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" onClick={() => setCrop({ kind: "avatar" })} data-testid="avatar-edit-btn">{me.avatar_url ? t("photo.change") : t("photo.device")}</button>
        {canCamera && <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" onClick={() => setCamera(true)} data-testid="avatar-camera-btn">📷 {t("photo.camera")}</button>}
      </div>
    </div>
    {me.avatar_url && <p className="text-xs text-slate-400">{t("photo.required")}</p>}
    {canCover && (<div className="space-y-2">
      <div className="w-full aspect-[3/1] rounded bg-slate-100 overflow-hidden">
        {me.cover_url
          ? <img src={me.cover_url} alt="" className="w-full h-full object-cover" data-testid="settings-cover" />
          : <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm" data-testid="settings-cover-placeholder">{t("photo.add_cover")}</div>}
      </div>
      <div className="flex gap-2 flex-wrap">
        <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" onClick={() => setCrop({ kind: "cover" })} data-testid="cover-edit-btn">{me.cover_url ? t("photo.change_cover") : t("photo.add_cover")}</button>
      </div>
      {me.cover_url && <p className="text-xs text-slate-400">{t("photo.required")}</p>}
    </div>)}
    {crop && <ImageCropModal kind={crop.kind} initialBlob={crop.blob || null} allowContain={me.role === "organization"}
              onUpload={upload(crop.kind, crop.source)} onClose={() => setCrop(null)} />}
    {camera && <CameraCapture onUse={(blob) => { setCamera(false); setCrop({ kind: "avatar", blob, source: "camera" }); }} onClose={() => setCamera(false)} />}
  </div>);
}

export function OrgEditor() {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [orig, setOrig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api.get("/me/organization").then(({data}) => {
    const v = {
      org_name: data.profile?.org_name || "", phone: data.profile?.phone || "",
      address_street: data.profile?.address_street || "", address_city: data.profile?.address_city || "",
      org_type: data.profile?.org_type || "", org_type_other: data.profile?.org_type_other || "",
      public_bio: data.profile?.public_bio || "",
      tax_id: data.profile?.tax_id || "", vat_id: data.profile?.vat_id || "",
      contact_name: data.contact?.contact_name || "", contact_role: data.contact?.contact_role || "",
      contact_email: data.contact?.contact_email || "", contact_phone: data.contact?.contact_phone || "",
    };
    setD(v); setOrig(v);
  }).catch(() => setD(null)); }, []);
  if (!d) return null;
  const s = (k, v) => { setD(prev => ({ ...prev, [k]: v })); setSaved(false); };
  const save = async () => {
    setSaving(true);
    try { await api.put("/me/organization", { ...d, vat_id: d.vat_id || null, org_type_other: d.org_type === "altro" ? (d.org_type_other || null) : null }); toast.success(t("auth.org_updated")); setSaved(true); setOrig(d); }
    catch(e) { toast.error(e.response?.data?.detail || t("events.error_generic")); }
    finally { setSaving(false); }
  };
  const cancel = () => { setD(orig); setSaved(false); };
  return (<div className="yp-card p-6 space-y-3 mt-4" data-testid="org-editor">
    <h3 className="font-semibold">{t("auth.org_data")}</h3>
    <div className="grid md:grid-cols-2 gap-3">
      <input className="yp-input" placeholder={t("auth.org_name")} value={d.org_name} onChange={e=>s("org_name",e.target.value)} data-testid="org-edit-name" />
      <input className="yp-input" placeholder={t("auth.org_phone")} value={d.phone} onChange={e=>s("phone",e.target.value)} data-testid="org-edit-phone" />
      <select className="yp-input" value={d.org_type} onChange={e=>s("org_type",e.target.value)} data-testid="org-edit-type" aria-label={t("orgx.select_type")}>
        <option value="">{t("orgx.select_type")}</option>
        {ORG_TYPE_CODES.map(c => <option key={c} value={c}>{t(`orgx.type_${c}`)}</option>)}
      </select>
      {d.org_type === "altro"
        ? <input className="yp-input" placeholder={t("orgx.type_other_ph")} value={d.org_type_other} onChange={e=>s("org_type_other",e.target.value)} data-testid="org-edit-type-other" />
        : <div className="hidden md:block" />}
      <input className="yp-input" placeholder={t("auth.address_street")} value={d.address_street} onChange={e=>s("address_street",e.target.value)} data-testid="org-edit-street" />
      <input className="yp-input" placeholder={t("auth.address_city")} value={d.address_city} onChange={e=>s("address_city",e.target.value)} data-testid="org-edit-city" />
      <input className="yp-input" placeholder={t("auth.org_tax_id")} value={d.tax_id} onChange={e=>s("tax_id",e.target.value)} data-testid="org-edit-tax" />
      <input className="yp-input" placeholder={t("auth.org_vat")} value={d.vat_id} onChange={e=>s("vat_id",e.target.value)} data-testid="org-edit-vat" />
    </div>
    <label className="text-sm font-medium text-slate-600">{t("orgx.public_bio")}</label>
    <textarea className="yp-input" rows={4} maxLength={2000} placeholder={t("orgx.org_bio_ph")} value={d.public_bio} onChange={e=>s("public_bio",e.target.value)} data-testid="org-edit-public-bio" />
    <h3 className="font-semibold pt-2">{t("auth.contact_section")}</h3>
    <div className="grid md:grid-cols-2 gap-3">
      <input className="yp-input" placeholder={t("auth.contact_name")} value={d.contact_name} onChange={e=>s("contact_name",e.target.value)} data-testid="org-edit-contact-name" />
      <input className="yp-input" placeholder={t("auth.contact_role")} value={d.contact_role} onChange={e=>s("contact_role",e.target.value)} data-testid="org-edit-contact-role" />
      <input className="yp-input" placeholder={t("auth.contact_email")} value={d.contact_email} onChange={e=>s("contact_email",e.target.value)} data-testid="org-edit-contact-email" />
      <input className="yp-input" placeholder={t("auth.contact_phone")} value={d.contact_phone} onChange={e=>s("contact_phone",e.target.value)} data-testid="org-edit-contact-phone" />
    </div>
    <div className="flex gap-2 items-center flex-wrap">
      <button className="yp-btn yp-btn-primary" onClick={save} disabled={saving} data-testid="org-edit-save">{saving ? t("common.loading") : t("common.save")}</button>
      <button className="yp-btn yp-btn-outline" onClick={cancel} disabled={saving} data-testid="org-edit-cancel">{t("common.cancel")}</button>
      {saved && <span className="text-sm text-green-600" role="status" data-testid="org-edit-saved">{t("settings.saved")}</span>}
    </div>
  </div>);
}

export function ProEditor() {
  const { t, i18n } = useTranslation();
  const u = getUser();
  const [d, setD] = useState(null);
  const [orig, setOrig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadError, setLoadError] = useState(false);
  useEffect(() => {
    api.get(`/professionals/${u.id}`).then(({ data }) => {
      const p = data.profile || {};
      const v = { professional_title: p.professional_title || "", profession: p.profession || "", specialization: p.specialization || "", board: p.board || "",
        board_number: p.board_number || "", vat_id: p.vat_id || "", bio: p.bio || "", public_bio: p.public_bio || "" };
      setD(v); setOrig(v);
    }).catch(() => setLoadError(true));
  }, []);
  if (loadError) return (
    <div className="yp-card p-6 mt-4 text-sm text-slate-500" role="alert" data-testid="pro-edit-load-error">{t("settings.pro_edit_unavailable")}</div>);
  if (!d) return null;
  const s = (k, v) => { setD(prev => ({ ...prev, [k]: v })); setSaved(false); };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/professional-profile", { ...d, professional_title: d.professional_title || null });
      toast.success(t("settings.saved"));
      setSaved(true); setOrig(d);
    } catch (e) { toast.error(e.response?.data?.detail || t("events.error_generic")); }
    finally { setSaving(false); }
  };
  const cancel = () => { setD(orig); setSaved(false); };
  return (<div className="yp-card p-6 space-y-3 mt-4" data-testid="pro-editor">
    <h3 className="font-semibold">{t("settings.pro_data")}</h3>
    <div className="grid md:grid-cols-2 gap-3">
      <select className="yp-input md:col-span-2" value={d.professional_title} onChange={e => s("professional_title", e.target.value)} data-testid="pro-edit-title" aria-label={t("auth.pro_title_label")}>
        <option value="">{t("auth.pro_title_label")}</option>
        <option value="doctor_m">{t("auth.pro_title_m")}</option>
        <option value="doctor_f">{t("auth.pro_title_f")}</option>
      </select>
      <input className="yp-input" placeholder={t("auth.pro_profession")} value={d.profession} onChange={e => s("profession", e.target.value)} data-testid="pro-edit-profession" />
      <input className="yp-input" placeholder={t("auth.pro_specialization")} value={d.specialization} onChange={e => s("specialization", e.target.value)} data-testid="pro-edit-specialization" />
      <input className="yp-input" placeholder={t("auth.pro_board")} value={d.board} onChange={e => s("board", e.target.value)} data-testid="pro-edit-board" />
      <input className="yp-input" placeholder={t("auth.pro_board_number")} value={d.board_number} onChange={e => s("board_number", e.target.value)} data-testid="pro-edit-board-number" />
      <input className="yp-input" placeholder={t("auth.pro_vat")} value={d.vat_id} onChange={e => s("vat_id", e.target.value)} data-testid="pro-edit-vat" />
    </div>
    <label className="text-sm font-medium text-slate-600">{t("orgx.public_bio")}</label>
    <textarea className="yp-input" rows={4} maxLength={2000} placeholder={t("orgx.pro_bio_ph")} value={d.public_bio} onChange={e => s("public_bio", e.target.value)} data-testid="pro-edit-public-bio" />
    <div className="flex gap-2 items-center flex-wrap">
      <button className="yp-btn yp-btn-primary" onClick={save} disabled={saving} data-testid="pro-edit-save">{saving ? t("common.loading") : t("common.save")}</button>
      <button className="yp-btn yp-btn-outline" onClick={cancel} disabled={saving} data-testid="pro-edit-cancel">{t("common.cancel")}</button>
      {saved && <span className="text-sm text-green-600" role="status" data-testid="pro-edit-saved">{t("settings.saved")}</span>}
    </div>
  </div>);
}

export function BadgeRequestSection() {
  const { t } = useTranslation();
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/badge-request/status").then(({ data }) => setSt(data)).catch(() => {}); }, []);
  if (!st || st.verified || (!st.can_request && !st.pending)) return null;
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/badge-request");
      if (data.status === "sent") { toast.success(t("badge.sent")); setSt({ ...st, pending: true }); }
      else if (data.status === "already_pending") { toast.info(t("badge.already")); setSt({ ...st, pending: true }); }
      setOpen(false);
    } catch { toast.error(t("events.error_generic")); }
    finally { setSending(false); }
  };
  return (<div className="yp-card p-6 mb-4" data-testid="badge-request-section">
    <div className="flex items-center gap-2">
      <h3 className="font-semibold">{t("badge.title_section")}</h3>
      <button type="button" onClick={() => setOpen(true)} data-testid="badge-request-help" aria-label={t("badge.help_aria")}
        className="w-5 h-5 rounded-full border border-slate-300 text-slate-500 text-xs font-semibold flex items-center justify-center hover:bg-slate-100 transition-colors">?</button>
    </div>
    {st.pending
      ? <p className="text-sm text-slate-500 mt-2" data-testid="badge-request-pending">{t("badge.already")}</p>
      : <p className="text-sm text-slate-400 mt-2">{t("badge.hint")}</p>}
    {open && createPortal((<div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="badge-request-modal" onClick={() => setOpen(false)}>
      <div className="bg-white rounded-xl p-6 max-w-md w-full space-y-4" onClick={e => e.stopPropagation()}>
        <h4 className="font-semibold text-lg">{t("badge.modal_title")}</h4>
        <p className="text-sm text-slate-600">{t("badge.modal_msg")}</p>
        <div className="flex gap-2 justify-end">
          <button className="yp-btn yp-btn-outline" onClick={() => setOpen(false)} data-testid="badge-request-cancel">{t("common.cancel")}</button>
          {!st.pending && <button className="yp-btn yp-btn-primary" onClick={send} disabled={sending} data-testid="badge-request-send">{sending ? t("common.loading") : t("badge.send_btn")}</button>}
        </div>
      </div>
    </div>), document.body)}
  </div>);
}

export function NotifPrefs() {
  const { t } = useTranslation();
  const [optOut, setOptOut] = useState(null);
  useEffect(() => { api.get("/auth/me").then(({ data }) => setOptOut(!!data.photo_reminder_opt_out)).catch(() => {}); }, []);
  if (optOut === null) return null;
  const toggle = async () => {
    const nv = !optOut; setOptOut(nv);
    try { await api.post("/me/notification-prefs", { photo_reminder_opt_out: nv }); toast.success(t("settings.saved")); }
    catch { setOptOut(!nv); toast.error(t("events.error_generic")); }
  };
  return (<div className="yp-card p-6 mb-4" data-testid="notif-prefs">
    <h3 className="font-semibold mb-2">{t("notifpref.title")}</h3>
    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
      <input type="checkbox" checked={!optOut} onChange={toggle} data-testid="photo-reminder-toggle" />
      {t("notifpref.photo_reminder")}
    </label>
  </div>);
}

export default function Settings() {
  const { t } = useTranslation();
  const u = getUser();
  const force = new URLSearchParams(window.location.search).get("force") === "1" || u?.must_change_password;
  const [cur, setCur] = useState(""); const [nw, setNw] = useState("");
  const change = async () => {
    try {
      const { data } = await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      if (data.token) {
        setToken(data.token);
        saveUser({ ...u, must_change_password: false });
        toast.success(t("settings.pw_updated"));
        setCur(""); setNw("");
        window.location.href = u?.role === "admin" ? "/admin" : "/feed";
      } else {
        logout();
      }
    } catch(e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const pause = async () => { if (!window.confirm(t("account.confirm_pause"))) return;
    try { await api.post("/auth/pause"); window.location.href = "/account-status"; } catch(e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const deact = async () => { if (!window.confirm(t("account.confirm_deact"))) return;
    try { await api.post("/auth/deactivate"); window.location.href = "/account-status"; } catch(e) { toast.error(e.response?.data?.detail || "Errore"); } };
  return (<Layout><div className="yp-container max-w-2xl py-8">
    <h1 className="text-3xl font-bold mb-6">{t("settings.title")}</h1>
    <div className="yp-card p-6 space-y-3 mb-4">
      <div><div className="text-xs text-slate-500">{t("settings.email")}</div><div>{u?.email}</div></div>
      {u?.role === "organization"
        ? <div data-testid="settings-account-type"><div className="text-xs text-slate-500">{t("orgx.account_type")}</div><div>{t("orgx.account_type_org")}</div></div>
        : <div><div className="text-xs text-slate-500">{t("settings.role")}</div><div>{u?.role}</div></div>}
      <div><div className="text-xs text-slate-500">{t("settings.nickname")}</div><div>{u?.nickname}</div></div>
    </div>
    <PhotoSection />
    <BadgeRequestSection />
    <NotifPrefs />
    <div className="yp-card p-6 space-y-3">
      {force && <div className="p-3 rounded bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="force-password-banner">{t("settings.force_banner")}</div>}
      <h3 className="font-semibold">{t("settings.change_pw")}</h3>
      <input className="yp-input" type="password" placeholder={t("settings.current_pw")} value={cur} onChange={e=>setCur(e.target.value)} data-testid="settings-current-pw" />
      <input className="yp-input" type="password" placeholder={t("settings.new_pw")} value={nw} onChange={e=>setNw(e.target.value)} data-testid="settings-new-pw" />
      <button className="yp-btn yp-btn-primary" onClick={change} data-testid="settings-change-pw">{t("settings.update_pw")}</button>
    </div>
    <div className="flex flex-col gap-2 mt-6 max-w-xs">
      <button className="yp-btn yp-btn-outline" onClick={pause} data-testid="settings-pause">{t("account.take_break")}</button>
      <button className="yp-btn yp-btn-ghost text-red-600" onClick={deact} data-testid="settings-deactivate">{t("settings.deactivate")}</button>
    </div>
    {u?.role === "professional" && <ProEditor />}
    {u?.role === "organization" && <OrgEditor />}
  </div></Layout>);
}
